/**
 * Minimal turn shape `calculationsTurn` reads. Wider LocalTurn objects satisfy this.
 */
export interface CalculationsTurnCandidate<T> {
  role: 'user' | 'assistant'
  streaming?: boolean
  stopped?: boolean
  refused?: boolean
  calculations?: Array<T>
}

/**
 * Calculations for the sources drawer (P1): the newest finished assistant
 * answer that computed any, under the same rules as `referencesTurn`. A
 * streaming answer keeps the previous calculations; the newest answer being
 * stopped or refused clears them.
 */
export function calculationsTurn<T>(turns: Array<CalculationsTurnCandidate<T>>): Array<T> {
  let hasSeenAssistant = false

  for (let i = turns.length - 1; i >= 0; i -= 1) {
    const turn = turns[i]
    if (turn.role !== 'assistant') {
      continue
    }

    const isNewestAssistant = !hasSeenAssistant
    hasSeenAssistant = true

    if (turn.streaming) {
      continue
    }
    if (turn.stopped || turn.refused) {
      if (isNewestAssistant) {
        return []
      }
      continue
    }
    if ((turn.calculations?.length ?? 0) > 0) {
      return turn.calculations ?? []
    }
  }

  return []
}

/** A calculation chip the reader clicked: which answer, which figure. */
export interface CalculationFocus {
  turnId: string
  evidenceId: string
  /** Bumps on every click, so clicking the same chip again re-reveals it. */
  nonce: number
}

/**
 * Calculations for the sources drawer when the reader followed a figure chip
 * (P1): the clicked answer's own calculations, so a cited number traces to its
 * own calculation even on an older answer. Without a focus, or when the
 * focused answer no longer holds that figure, it is `calculationsTurn`.
 */
export function calculationsFor<T extends { id: string }>(
  turns: Array<CalculationsTurnCandidate<T> & { id?: string }>,
  focus: Pick<CalculationFocus, 'turnId' | 'evidenceId'> | null,
): Array<T> {
  if (focus) {
    const focused = turns.find((turn) => turn.id === focus.turnId)
    const calculations = focused?.calculations ?? []
    if (calculations.some((calculation) => calculation.id === focus.evidenceId)) {
      return calculations
    }
  }

  return calculationsTurn(turns)
}
